import React, { MutableRefObject, RefObject, useEffect, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/lib/hooks/use-toast";
import { Button } from "./button";
import { useRef } from "react";
import { useQuran } from "@/app/providers/QuranContextProvider";
import useLocalStorage from "@/lib/hooks/useLocalStorage";
import { LOCALSTORAGE_NOTES_KEY } from "@/lib/constants";
import { saveUserNote } from "@/actions/save-note";
import { getUserNote } from "@/actions/get-user-note";
import { Spinner } from "./spinner";
import useIntersectionObserver from "@/lib/hooks/useIntersectionObserver";

function QuranAyahNote({
  id,
  surah,
  verse,
  children,
}: {
  id: string | number;
  verse: number;
  surah: number;
  children: React.ReactNode;
}) {
  const textarea = useRef<HTMLTextAreaElement>(null);
  const [notes, setNotes] = useLocalStorage<{ [key: string]: string }>(
    LOCALSTORAGE_NOTES_KEY,
    { "0": "" },
  );
  const [userNote, setUserNote] = useState<string>("..Loading..");
  const { isLoggedIn } = useQuran();
  const [ref, isInView] = useIntersectionObserver();

  const handleNotSave = async () => {
    if (textarea.current && textarea.current.value == "") return;

    let data = { "0": "" };
    if (textarea.current) {
      data = { ...data, [id]: textarea.current.value };

      if (!isLoggedIn) {
        setNotes({ ...notes, ...data });
      } else {
        setNotes({ ...notes, ...data });
        const resp = await saveUserNote({
          verseId: parseInt(id.toString()),
          verse: verse,
          surah: surah,
          noteData: textarea.current.value,
        });

        if (resp.success) {
          toast({
            title: "Note Saved",
            description: "Your note has been successfully saved.",
          });
        } else {
          toast({
            variant: "destructive",
            title: "Uh oh! Something went wrong.",
            description: resp.error?.message,
          });
        }
      }
    }
  };

  useEffect(() => {
    if (!isInView) return;

    let userNote = undefined;
    if (!isLoggedIn) userNote = notes[id];

    const fetch = async () => {
      if (!isLoggedIn) {
        setUserNote("");
        textarea.current?.focus();
        return;
      }

      const resp = await getUserNote(parseInt(id.toString()));
      if (resp.success && resp.note) {
        const text = resp.note;
        setNotes({ ...notes, [id]: text });
        setUserNote(text);
      } else {
        setUserNote("");
      }
      textarea.current?.focus();
    };

    if (userNote) {
      setUserNote(userNote);
    } else fetch();

    //eslint-disable-next-line
  }, [isInView]);

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Take Note</DialogTitle>
          <DialogDescription>
            {!isLoggedIn
              ? "You are not logged in, so your notes may be lost. Please log in for persistent storage"
              : "Note..."}
          </DialogDescription>
        </DialogHeader>
        <div ref={ref} className="grid gap-4 py-4">
          <div className=" w-full grid grid-cols-1 items-center gap-4">
            {userNote === "..Loading.." ? (
              <div className="h-30 text-center flex items-center justify-center">
                <Spinner className="text-primary" size={"large"}>
                  <span>Fetching from server if the note exists......</span>
                </Spinner>
              </div>
            ) : (
              <Textarea
                ref={textarea}
                id="ayah-note"
                className="col-span-3"
                defaultValue={userNote}
              />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button type="submit" onClick={handleNotSave}>
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default QuranAyahNote;
